import type { MaybeRefOrGetter } from 'vue';
import { DxDataGrid } from 'devextreme-vue/data-grid';
import { useDataGridExport } from '~/composables/data-grid/useDataGridExport';
import type { UseDataGridExportOptions } from '~/composables/data-grid/useDataGridExport';
import { useDateRangeShortcuts } from '~/composables/data-grid/useDateRangeShortcuts';
import { useDataGridDateRange } from '~/composables/data-grid/useDataGridDateRange';
import { useDataGridSearch } from '~/composables/data-grid/useDataGridSearch';
import { useDataGridInteractions } from '~/composables/data-grid/useDataGridInteractions';
import { useDataGridFormatters } from '~/composables/data-grid/useDataGridFormatters';
import type { ExportCellFormatters } from '~/types';

export const useDataGrid = (
  exportFileName?: MaybeRefOrGetter<string | undefined>,
  exportCellFormatters?: ExportCellFormatters,
  loadAllRowsForExport?: UseDataGridExportOptions['loadAllRowsForExport']
) => {
  const { $i18n } = useNuxtApp();
  const { t } = $i18n;
  const dataGridRef = ref<DxDataGrid>(DxDataGrid);
  const dataGrid = computed(() => dataGridRef.value?.instance);

  const { exportData } = useDataGridExport({
    exportFileName,
    dataGrid,
    exportCellFormatters,
    loadAllRowsForExport
  });
  const { shortcuts } = useDateRangeShortcuts();
  const { dateFormat, handleDataGridDate, getCurrency, getStyle } = useDataGridFormatters();
  const {
    initDateRange,
    updateDate,
    resetDateRange,
    resolveShortcutRange,
    getDateFilterFromLocalStorage,
    saveDateFilterInLocalStorage
  } = useDataGridDateRange({ dataGrid, shortcuts, dateFormat });
  const { handleSearchValue } = useDataGridSearch({ dataGrid });
  const { onContextMenuPreparing, showColumnChooser } = useDataGridInteractions({ dataGridRef });

  const mapSenderDisplayTextToId = (displayText: string) => {
    const lowerDisplayText = displayText.toLocaleLowerCase();

    if ('lemonway'.includes(lowerDisplayText)) {
      return 4;
    } else if (t('navigation.myWallet').toLocaleLowerCase().includes(lowerDisplayText)) {
      return 11;
    } else {
      return null;
    }
  };

  return {
    onContextMenuPreparing,
    showColumnChooser,
    handleSearchValue,
    exportData,
    mapSenderDisplayTextToId,
    getDateFilterFromLocalStorage,
    saveDateFilterInLocalStorage,
    initDateRange,
    updateDate,
    resetDateRange,
    resolveShortcutRange,
    getStyle,
    handleDataGridDate,
    getCurrency,
    dataGridRef,
    dataGrid,
    dateFormat,
    shortcuts
  };
};
